import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Badge } from 'reactstrap';
import {
  OBJECTIVE_TYPE_SCOPES,
  ObjectivePerson,
} from '../../utils/models/Objective';
import { getContrastColor } from '../../utils/util/color';
import { ObjectiveType } from './ObjectiveTypeOperations';
import ObjectiveTypePopover from './ObjectiveTypePopover';

interface Props {
  objectiveTypeId: number | null | undefined;
  organizationObjectiveTypes: ObjectiveType[];
  collaborators?: ObjectivePerson[];
  isEditable?: boolean;
  onChangeType?: (typeId: number | null) => void;
  userEditableObjectiveTypes?: ObjectiveType[];
  childrenTypeIds?: (number | null | undefined)[];
  parentTypeId?: number | null | undefined;
}

const EMPTY_TYPES: ObjectiveType[] = [];
const EMPTY_IDS: (number | null | undefined)[] = [];

const ObjectiveTypeBadge: FC<Props> = ({
  objectiveTypeId,
  organizationObjectiveTypes,
  collaborators: _collaborators,
  isEditable = false,
  onChangeType,
  userEditableObjectiveTypes = EMPTY_TYPES,
  childrenTypeIds = EMPTY_IDS,
  parentTypeId,
}) => {
  const badgeRef = useRef<HTMLSpanElement>(document.createElement('span'));
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const isClickable = !!(isEditable && onChangeType);

  const objectiveType = useMemo(
    () => organizationObjectiveTypes.find((t) => t.id === objectiveTypeId),
    [organizationObjectiveTypes, objectiveTypeId]
  );

  const toggle = useCallback(() => setIsPopoverOpen((prev) => !prev), []);
  const onClose = useCallback(() => setIsPopoverOpen(false), []);
  const onBadgeClick = useCallback(() => {
    if (isClickable) setIsPopoverOpen((prev) => !prev);
  }, [isClickable]);

  if (!objectiveTypeId || organizationObjectiveTypes.length === 0) {
    return null;
  }

  if (
    !objectiveType ||
    objectiveType.scope === OBJECTIVE_TYPE_SCOPES.INDIVIDUAL
  ) {
    return null;
  }

  const displayName = objectiveType.name;
  const popoverText = objectiveType.description || displayName;

  const clickableClass = isClickable ? ' objective-type-badge-clickable' : '';
  const clickableStyle: React.CSSProperties | undefined = isClickable
    ? { cursor: 'pointer' }
    : undefined;

  const badge = objectiveType.color ? (
    <span
      ref={badgeRef}
      role={isClickable ? 'button' : undefined}
      onClick={onBadgeClick}
      className={
        'badge rounded-pill d-inline-flex align-items-center' + clickableClass
      }
      style={{
        backgroundColor: objectiveType.color,
        color: getContrastColor(objectiveType.color),
        ...clickableStyle,
      }}
    >
      {displayName}
    </span>
  ) : (
    <Badge
      className={
        'rounded-pill bg-primary-soft d-inline-flex align-items-center' +
        clickableClass
      }
      innerRef={badgeRef}
      role={isClickable ? 'button' : undefined}
      onClick={onBadgeClick}
      style={clickableStyle}
    >
      {displayName}
    </Badge>
  );

  return (
    <>
      {badge}
      {isClickable && onChangeType ? (
        <ObjectiveTypePopover
          target={badgeRef}
          isOpen={isPopoverOpen}
          toggle={toggle}
          onClose={onClose}
          currentTypeId={objectiveTypeId}
          onChangeType={onChangeType}
          userEditableObjectiveTypes={userEditableObjectiveTypes}
          organizationObjectiveTypes={organizationObjectiveTypes}
          childrenTypeIds={childrenTypeIds}
          parentTypeId={parentTypeId}
        />
      ) : (
        <UncontrolledPopover
          placement="top"
          target={badgeRef}
          trigger="hover legacy"
          delay={{ show: 60, hide: 300 }}
        >
          <span className="small">{popoverText}</span>
        </UncontrolledPopover>
      )}
    </>
  );
};

export default React.memo(ObjectiveTypeBadge);
